<template>
  <div class="price-alert-card card">
    <h3>Price Alert</h3>
    <p class="alert-description">Get notified when the price drops to your target price</p>
    
    <!-- CP-134: formKey bumps after a successful create so the form remounts
         empty and the email helper returns without a page reload. -->
    <form :key="formKey" @submit.prevent="createAlert" class="alert-form">
      <div class="form-group">
        <label for="email">Email Address</label>
        <input 
          type="email" 
          id="email"
          v-model="email" 
          @change="syncEmail"
          required
          placeholder="your.email@example.com"
          class="form-input"
        />
      </div>
      
      <div class="form-group">
        <label for="targetPrice">Target Price (optional)</label>
        <input 
          type="number" 
          id="targetPrice"
          v-model.number="targetPrice" 
          placeholder="Enter target price"
          class="form-input"
        />
        <small class="form-help">Leave empty to be notified of any price drop</small>
      </div>
      
      <div class="form-group">
        <label for="cabinType">Cabin Type (optional)</label>
        <select 
          id="cabinType"
          v-model="cabinType" 
          class="form-select"
        >
          <option value="">All Cabin Types</option>
          <option v-for="type in cabinTypes" :key="type" :value="type">
            {{ type }}
          </option>
        </select>
      </div>
      
      <!-- CP-134: submit stays disabled until an email is entered; say why. -->
      <div class="submit-group">
        <button 
          type="submit" 
          :disabled="loading || emailMissing"
          :aria-describedby="emailMissing ? 'price-alert-email-hint' : null"
          class="submit-btn"
        >
          <span v-if="loading">Creating...</span>
          <span v-else>Create Price Alert</span>
        </button>
        <small v-if="emailMissing" id="price-alert-email-hint" class="form-help submit-hint">
          Enter your email address to create an alert.
        </small>
      </div>
    </form>
    
    <div v-if="success" class="success-message">
      <i class="fas fa-check-circle"></i>
      <p>{{ success }}</p>
    </div>
    
    <div v-if="error" class="error-message">
      <i class="fas fa-exclamation-circle"></i>
      <p>{{ error }}</p>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import api from '@/services/api';
import { rememberAlert } from '@/utils/myAlerts';

export default {
  name: 'PriceAlert',
  props: {
    cruiseId: {
      type: Number,
      required: true
    },
    cabinTypes: {
      type: Array,
      default: () => []
    }
  },
  emits: ['alert-created'],
  setup(props, { emit }) {
    const email = ref('');
    const targetPrice = ref(null);
    const cabinType = ref('');
    const loading = ref(false);
    const success = ref('');
    const error = ref('');
    // CP-134: remount key for the form (bumped after a successful create).
    const formKey = ref(0);
    // CP-134: single source of truth for "no email yet" (disabled, aria, helper).
    const emailMissing = computed(() => !String(email.value ?? '').trim());
    
    // CP-134: some autofill paths only fire `change`; keep the model in sync.
    const syncEmail = (event) => {
      const value = event?.target?.value ?? '';
      if (value !== email.value) {
        email.value = value;
      }
    };
    
    const createAlert = async () => {
      loading.value = true;
      error.value = '';
      success.value = '';
      
      try {
        const data = {
          cruise_id: props.cruiseId,
          email: email.value,
          target_price: targetPrice.value || null,
          cabin_type: cabinType.value || null
        };
        
        const response = await api.createPriceAlert(data);
        // CP-246: keep this alert's cancel token in this browser so Manage Alerts
        // can show and cancel it (the server no longer lists alerts by email).
        rememberAlert(typeof window !== 'undefined' ? window.localStorage : null, {
          id: response?.data?.id,
          cruiseId: props.cruiseId,
          targetPrice: data.target_price,
          cabinType: data.cabin_type,
          token: response?.data?.cancel_token
        });
        success.value = 'Price alert created successfully! You will be notified when the price changes.';
        email.value = '';
        targetPrice.value = null;
        cabinType.value = '';
        formKey.value += 1;
        emit('alert-created');
      } catch (err) {
        error.value = err.response?.data?.error || 'Failed to create price alert. Please try again.';
      } finally {
        loading.value = false;
      }
    };
    
    return {
      email,
      targetPrice,
      cabinType,
      loading,
      success,
      error,
      formKey,
      emailMissing,
      syncEmail,
      createAlert
    };
  }
};
</script>

<style scoped>
.price-alert-card {
  margin-bottom: 24px;
  /* CP-120: Detail rail CTA jumps here; clear the sticky site header. */
  scroll-margin-top: 88px;
}

.price-alert-card h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: var(--cs-d-text);
}

.alert-description {
  margin: 0 0 24px 0;
  color: var(--cs-d-muted);
  font-size: 14px;
}

.alert-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-size: 14px;
  font-weight: 500;
  color: var(--cs-d-text);
}

.form-input,
.form-select {
  padding: 10px 12px;
  border: 1px solid var(--cs-d-border);
  border-radius: 6px;
  font-size: 14px;
  color: var(--cs-d-text);
  transition: border-color 0.2s;
}

/* CP-225 Detail follow-up (dark only, so the light fields keep the browser's own colours): the fields had
   no background of their own, so they stayed a light box (select #efefef) under the now-light text. */
[data-theme="dark"] .form-input,
[data-theme="dark"] .form-select {
  background-color: var(--cs-d-surface);
  color-scheme: dark;
}

/* CP-249: both themes (was dark only; the light placeholder was Tailwind preflight's gray-400 #9ca3af, 2.54:1).
   opacity: 1 so no browser fades it (Firefox's own default is 54%). */
.form-input::placeholder {
  color: var(--cs-d-placeholder);
  opacity: 1;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: var(--cs-d-link);
}

.form-help {
  font-size: 12px;
  color: var(--cs-d-muted);
}

.submit-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.submit-hint {
  text-align: center;
}

.submit-btn {
  padding: 12px 24px;
  /* CP-120: brand primary so the rail's real action matches the CTA above */
  background-color: var(--cs-fill);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.submit-btn:hover:not(:disabled) {
  background-color: var(--cs-fill-hover);
}

.submit-btn:disabled {
  background-color: var(--cs-d-disabled);
  cursor: not-allowed;
}

.success-message,
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 6px;
  margin-top: 16px;
}

.success-message {
  background-color: var(--cs-d-success-bg);
  color: var(--cs-d-success);
}

.error-message {
  background-color: var(--cs-d-error-bg);
  color: var(--cs-d-error);
}

.success-message i,
.error-message i {
  font-size: 18px;
}

.success-message p,
.error-message p {
  margin: 0;
  font-size: 14px;
}
</style>

